import { legacyRoutes } from "../generated/routeManifest";

export type EducationTopic = {
  href: string;
  title: string;
  subject: string;
  area: string;
  level?: string;
};

export type EducationSubject = {
  slug: string;
  href: string;
  title: string;
  icon: string;
  intro: string;
};

export const educationSubjects: EducationSubject[] = [
  {
    slug: "italian",
    href: "/education/italian",
    title: "Italiano",
    icon: "language",
    intro: "Grammatica, scrittura, temi, poesia e analisi del testo."
  },
  {
    slug: "mathematics",
    href: "/education/mathematics",
    title: "Matematica",
    icon: "calculator",
    intro: "Percorso completo da aritmetica e geometria fino alla matematica per ingegneria informatica."
  },
  {
    slug: "history",
    href: "/education/history",
    title: "Storia",
    icon: "history",
    intro: "Dalle civiltà antiche al mondo contemporaneo, con eventi e timeline interattive."
  },
  {
    slug: "geography",
    href: "/education/geography",
    title: "Geografia",
    icon: "globe",
    intro: "Terra, fenomeni naturali, vulcani, placche e ciclo dell'acqua."
  },
  {
    slug: "science",
    href: "/education/science",
    title: "Scienze",
    icon: "flask",
    intro: "Materia, chimica, spazio, stagioni e osservazione scientifica."
  },
  {
    slug: "physics",
    href: "/education/physics",
    title: "Fisica",
    icon: "zap",
    intro: "Misure, moto, forze, energia, onde e termodinamica."
  },
  {
    slug: "music",
    href: "/education/music",
    title: "Musica",
    icon: "music",
    intro: "Note, frequenze, scale, accordi e lettura dello spartito."
  },
  {
    slug: "english",
    href: "/education/english",
    title: "English",
    icon: "language",
    intro: "Grammar, vocabulary, course units and guided practice."
  },
  {
    slug: "spanish",
    href: "/education/spanish",
    title: "Spagnolo",
    icon: "language",
    intro: "Grammatica, vocabolario e corso progressivo."
  },
  {
    slug: "chinese",
    href: "/education/chinese/course",
    title: "Cinese",
    icon: "language",
    intro: "Corso introduttivo con lessico, frasi e pratica guidata."
  },
  {
    slug: "japanese",
    href: "/education/japanese/course",
    title: "Giapponese",
    icon: "language",
    intro: "Corso introduttivo con lessico, frasi e pratica guidata."
  },
  {
    slug: "informatica",
    href: "/education/informatica",
    title: "Informatica",
    icon: "code",
    intro: "Basi digitali, programmazione, editor Turtle, BASIC e Z80."
  },
  {
    slug: "biology",
    href: "/education/biology",
    title: "Biologia",
    icon: "leaf",
    intro: "Cellule, DNA, piante e reti naturali."
  },
  {
    slug: "memory",
    href: "/education/memory",
    title: "Memoria",
    icon: "brain",
    intro: "Tecniche di studio, memorizzazione e giochi."
  }
];

const curatedEducationTopics: EducationTopic[] = [
  { href: "/education/italian/verbi", title: "Verbi", subject: "italian", area: "Verbi" },
  { href: "/education/italian/verbi/introduzione", title: "Introduzione ai verbi", subject: "italian", area: "Verbi" },
  { href: "/education/italian/verbi/modi-tempi", title: "Modi e tempi", subject: "italian", area: "Verbi" },
  { href: "/education/italian/verbi/forme-indefinite", title: "Forme indefinite", subject: "italian", area: "Verbi" },
  { href: "/education/italian/verbi/esercizi", title: "Esercizi sui verbi", subject: "italian", area: "Verbi" },
  { href: "/education/italian/temi", title: "Temi", subject: "italian", area: "Scrittura" },
  { href: "/education/italian/temi/teoria", title: "Teoria del tema", subject: "italian", area: "Scrittura" },
  { href: "/education/italian/temi/esempi", title: "Esempi di temi", subject: "italian", area: "Scrittura" },
  { href: "/education/italian/temi/esercizi", title: "Esercizi sui temi", subject: "italian", area: "Scrittura" },
  { href: "/education/italian/grammar-analysis", title: "Analisi grammaticale", subject: "italian", area: "Grammatica" },
  { href: "/education/italian/grammar-analysis/admin", title: "Gestione frasi", subject: "italian", area: "Grammatica" },
  { href: "/education/italian/poesie", title: "Poesie", subject: "italian", area: "Poesia" },
  { href: "/education/italian/poesie/teoria", title: "Teoria della poesia", subject: "italian", area: "Poesia" },
  { href: "/education/italian/poesie/componimenti", title: "Componimenti celebri", subject: "italian", area: "Poesia" },
  { href: "/education/italian/poesie/scrivere", title: "Scrivere una poesia", subject: "italian", area: "Poesia" },
  { href: "/education/italian/poesie/memorizzazione", title: "Memorizzare una poesia", subject: "italian", area: "Poesia" },

  { href: "/education/mathematics/primary-logic", title: "Logica e numeri", subject: "mathematics", area: "Primaria", level: "Primaria" },
  { href: "/education/mathematics/natural-numbers", title: "Numeri naturali", subject: "mathematics", area: "Primaria", level: "Primaria" },
  { href: "/education/mathematics/measurements-problems", title: "Misure e problemi", subject: "mathematics", area: "Primaria", level: "Primaria" },
  { href: "/education/mathematics/notebook-management", title: "Gestione del quaderno", subject: "mathematics", area: "Metodo di studio", level: "Primaria" },
  { href: "/education/mathematics/arithmetic/addition", title: "Addizione", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/arithmetic/subtraction", title: "Sottrazione", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/arithmetic/multiplication", title: "Moltiplicazione", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/arithmetic/division", title: "Divisione", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/arithmetic/division/concepts", title: "Concetti della divisione", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/expressions", title: "Espressioni", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/primes", title: "Numeri primi", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/divisibility", title: "Divisibilita", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/factorization", title: "Scomposizione", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/lcm", title: "mcm", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/gcd", title: "MCD", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/fractions", title: "Frazioni", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/rational-decimals", title: "Razionali e decimali", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/repeating-decimals", title: "Numeri periodici in frazione", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/relative-integers", title: "Numeri interi relativi", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/percentages", title: "Percentuali", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/fractions-percentages", title: "Frazioni e percentuali", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/proportions", title: "Proporzioni", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/percentage-applications", title: "Sconti e interessi", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/powers-roots", title: "Operazioni", subject: "mathematics", area: "Potenze" },
  { href: "/education/mathematics/advanced-powers", title: "Potenze con segni e frazioni", subject: "mathematics", area: "Potenze" },
  { href: "/education/mathematics/square-root-estimation", title: "Radice quadrata", subject: "mathematics", area: "Potenze" },
  { href: "/education/mathematics/tabelline", title: "Tabelline", subject: "mathematics", area: "Aritmetica" },
  { href: "/education/mathematics/sets", title: "Insiemi", subject: "mathematics", area: "Logica" },
  { href: "/education/mathematics/geometry", title: "Geometria", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/foundations", title: "Enti fondamentali", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/angoli", title: "Gli angoli", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/cartesian-plane", title: "Piano cartesiano", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/piana", title: "Geometria piana", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/tridimensionale", title: "Geometria tridimensionale", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/pitagora", title: "Teorema di Pitagora", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/triangle-congruence", title: "Congruenza dei triangoli", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/similarity-talete", title: "Similitudine e Talete", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/transformations", title: "Trasformazioni", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/geometry/proofs", title: "Dimostrazioni", subject: "mathematics", area: "Geometria" },
  { href: "/education/mathematics/algebra", title: "Algebra", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/monomials", title: "Monomi", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/first-degree", title: "Equazioni di primo grado", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/first-degree-problems", title: "Problemi di primo grado", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/second-degree", title: "Equazioni di secondo grado", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/systems", title: "Sistemi", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/inequalities", title: "Disequazioni", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/polynomials", title: "Polinomi", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/polynomial-multiplication", title: "Moltiplicazione di polinomi", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/algebra/special-products", title: "Prodotti notevoli", subject: "mathematics", area: "Algebra" },
  { href: "/education/mathematics/functions", title: "Funzioni", subject: "mathematics", area: "Superiori", level: "Superiori" },
  { href: "/education/mathematics/trigonometry", title: "Trigonometria", subject: "mathematics", area: "Superiori", level: "Superiori" },
  { href: "/education/mathematics/exponential-logarithms", title: "Esponenziali e logaritmi", subject: "mathematics", area: "Superiori", level: "Superiori" },
  { href: "/education/mathematics/analytic-geometry", title: "Geometria analitica", subject: "mathematics", area: "Superiori", level: "Superiori" },
  { href: "/education/mathematics/calculus", title: "Analisi", subject: "mathematics", area: "Superiori", level: "Superiori" },
  { href: "/education/mathematics/probability-statistics-advanced", title: "Probabilita avanzata", subject: "mathematics", area: "Superiori", level: "Superiori" },
  { href: "/education/mathematics/complex-numbers", title: "Numeri complessi", subject: "mathematics", area: "Superiori", level: "Superiori" },
  { href: "/education/mathematics/linear-algebra", title: "Algebra lineare", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/discrete-math", title: "Matematica discreta", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/advanced-calculus-optimization", title: "Ottimizzazione", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/advanced-probability", title: "Processi probabilistici", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/data-science-statistics", title: "Statistica per data science", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/graph-theory-algorithms", title: "Grafi e algoritmi", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/information-theory", title: "Teoria dell'informazione", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/cryptography-math", title: "Crittografia matematica", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/numerical-methods", title: "Metodi numerici", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/machine-learning-math", title: "Matematica per machine learning", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/computational-logic", title: "Logica computazionale", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/computability-complexity", title: "Computabilita e complessita", subject: "mathematics", area: "Ingegneria informatica", level: "Universita" },
  { href: "/education/mathematics/statistics-probability", title: "Statistica e probabilita", subject: "mathematics", area: "Dati e probabilita" },
  { href: "/education/mathematics/statistical-graphs", title: "Grafici statistici", subject: "mathematics", area: "Dati e probabilita" },

  { href: "/education/geography/volcano", title: "Vulcani", subject: "geography", area: "Terra" },
  { href: "/education/geography/plate-tectonics", title: "Tettonica a zolle", subject: "geography", area: "Terra" },
  { href: "/education/geography/water-cycle", title: "Ciclo dell'acqua", subject: "geography", area: "Terra" },

  { href: "/education/science/periodic-table", title: "Tavola periodica", subject: "science", area: "Chimica" },
  { href: "/education/science/states-of-matter", title: "Stati della materia", subject: "science", area: "Fisica e chimica" },
  { href: "/education/science/chemical-formula", title: "Formule chimiche", subject: "science", area: "Chimica" },
  { href: "/education/science/solar-system", title: "Sistema Solare", subject: "science", area: "Astronomia" },
  { href: "/education/science/seasons", title: "Le stagioni", subject: "science", area: "Astronomia" },

  { href: "/education/physics/misure", title: "Misure e unita SI", subject: "physics", area: "Fisica" },
  { href: "/education/physics/cinematica", title: "Cinematica", subject: "physics", area: "Fisica" },
  { href: "/education/physics/dinamica", title: "Dinamica", subject: "physics", area: "Fisica" },
  { href: "/education/physics/energia", title: "Lavoro ed energia", subject: "physics", area: "Fisica" },
  { href: "/education/physics/onde", title: "Onde e suono", subject: "physics", area: "Fisica" },
  { href: "/education/physics/constants", title: "Costanti universali", subject: "physics", area: "Fisica" },
  { href: "/education/physics/termodinamica", title: "Termodinamica", subject: "physics", area: "Fisica" },
  { href: "/education/physics/forze", title: "Forze fondamentali", subject: "physics", area: "Fisica" },
  { href: "/education/physics/temperatura", title: "Temperatura", subject: "physics", area: "Fisica" },

  { href: "/education/music/note", title: "Note e frequenze", subject: "music", area: "Teoria musicale" },
  { href: "/education/music/scale", title: "Scale musicali", subject: "music", area: "Teoria musicale" },
  { href: "/education/music/accordi", title: "Accordi", subject: "music", area: "Teoria musicale" },
  { href: "/education/music/spartito", title: "Spartito interattivo", subject: "music", area: "Pratica" },

  { href: "/education/english/course", title: "Corso completo", subject: "english", area: "Course" },
  { href: "/education/english/grammar", title: "Grammar", subject: "english", area: "Grammar" },
  { href: "/education/english/vocabulary", title: "Vocabulary", subject: "english", area: "Vocabulary" },
  { href: "/education/spanish/course", title: "Corso completo", subject: "spanish", area: "Corso" },
  { href: "/education/spanish/grammar", title: "Grammatica", subject: "spanish", area: "Grammatica" },
  { href: "/education/spanish/vocabulary", title: "Vocabolario", subject: "spanish", area: "Vocabolario" },
  { href: "/education/chinese/course", title: "Corso completo di cinese", subject: "chinese", area: "Corso" },
  { href: "/education/japanese/course", title: "Corso completo di giapponese", subject: "japanese", area: "Corso" },

  { href: "/education/informatica/base", title: "Le basi", subject: "informatica", area: "Fondamenti" },
  { href: "/education/informatica/programmazione", title: "Programmazione", subject: "informatica", area: "Programmazione" },
  { href: "/education/informatica/programmazione/turtle", title: "Editor Turtle", subject: "informatica", area: "Programmazione" },
  { href: "/education/informatica/programmazione/basic", title: "Editor BASIC", subject: "informatica", area: "Programmazione" },
  { href: "/education/informatica/programmazione/z80", title: "Editor Z80 ASM", subject: "informatica", area: "Programmazione" },

  { href: "/education/biology/cellule", title: "Cellule e DNA", subject: "biology", area: "Biologia" },
  { href: "/education/biology/piante", title: "Piante e Wood Wide Web", subject: "biology", area: "Biologia" },

  { href: "/education/memory/games", title: "Giochi di memoria", subject: "memory", area: "Giochi" },
  { href: "/education/memory/metodi", title: "Metodi di memoria", subject: "memory", area: "Tecniche" },
  { href: "/education/memory/come-studiare", title: "Studio guidato", subject: "memory", area: "Studio" },
  { href: "/education/memory/srs", title: "Ripasso spaziato", subject: "memory", area: "Studio" }
];

const humanize = (value: string) => value
  .replaceAll("-", " ")
  .split(" ")
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(" ");

const curatedPaths = new Set(curatedEducationTopics.map((topic) => topic.href));
const generatedEducationTopics: EducationTopic[] = legacyRoutes
  .map((entry) => entry.route)
  .filter((route) => route.startsWith("/education/") && !route.includes("{") && route.split("/").filter(Boolean).length > 2)
  .filter((route) => !curatedPaths.has(route) && route !== "/education/knowledge" && !route.startsWith("/education/history/timeline"))
  .map((href) => {
    const parts = href.split("/").filter(Boolean);
    return {
      href,
      title: humanize(parts.at(-1) ?? "Lezione"),
      subject: parts[1],
      area: humanize(parts[2] ?? "Percorso")
    };
  });

export const educationTopics: EducationTopic[] = [...curatedEducationTopics, ...generatedEducationTopics];

export const findSubject = (path: string) => educationSubjects.find((subject) => subject.href === path || path === `/education/${subject.slug}`);

export const findTopic = (path: string) => {
  const normalized = path.endsWith("/exercises") ? path.slice(0, -"/exercises".length) : path;
  const exact = educationTopics.find((topic) => topic.href === normalized);
  if (exact) return exact;
  const template = legacyRoutes.find((entry) => {
    if (!entry.route.startsWith("/education/") || !entry.route.includes("{")) return false;
    const marked = entry.route.replace(/\{[^}]+\}/g, "___PARAM___");
    const regex = marked.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replaceAll("___PARAM___", "[^/]+");
    return new RegExp(`^${regex}/?$`, "i").test(normalized);
  });
  if (!template) return undefined;
  const parts = normalized.split("/").filter(Boolean);
  return { href: normalized, title: humanize(parts.at(-1) ?? "Lezione"), subject: parts[1], area: humanize(parts[2] ?? "Percorso") };
};

export const topicsForSubject = (slug: string) => educationTopics.filter((topic) => topic.subject === slug);

export const groupedTopics = (slug: string) => {
  const groups = new Map<string, EducationTopic[]>();
  for (const topic of topicsForSubject(slug)) {
    const current = groups.get(topic.area) ?? [];
    current.push(topic);
    groups.set(topic.area, current);
  }
  return Array.from(groups.entries()).map(([area, topics]) => ({ area, topics }));
};
